/* screens/academy/CourseBuyPage.jsx — страница оплаты покупки курса (mock-режим).
   /learning/buy/:invoiceId. На деве (PAYMENT_PROVIDER=mock) сюда ведёт checkout;
   на проде покупка уходит на Робокассу и эта страница не используется.
   «Оплатить» → mock-callback → запись на курс → /learning/c/:id?purchase=… */
import React from 'react';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import * as api from '../../lib/api/academy.js';
import { academyErrorText } from '../../lib/academy/errorCodes.js';

const { useState } = React;
const { useFakeQuery } = hooks;
const { useParams, useNavigate } = RR;

function money(v) {
  return new Intl.NumberFormat('ru-RU').format(Math.round(Number(v || 0))) + ' ₽';
}

function CourseBuyPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(null); // 'success' | 'fail'
  const q = useFakeQuery('academy-purchase-' + invoiceId, () => api.getPurchaseStatus(invoiceId), [invoiceId]);
  const p = q.data;

  const act = async (outcome) => {
    setBusy(outcome);
    try {
      await api.mockCompletePurchase(invoiceId, outcome);
      if (outcome === 'success') {
        navigate(`/learning/c/${p.course_id}?purchase=${encodeURIComponent(invoiceId)}`);
      } else {
        toast.show('Оплата отменена', 'muted');
        navigate(`/learning/c/${p.course_id}`);
      }
    } catch (e) {
      // AD-1: это страница ВОЗВРАТА С ОПЛАТЫ — немой тост здесь читается как
      // «деньги пропали». DRF-ошибка поля приезжает без `detail`, поэтому одного
      // e?.detail мало: пропускаем через реестр кодов (он же добавит «что делать»).
      toast.show(academyErrorText(e, 'Не удалось обработать оплату'), 'danger');
      setBusy(null);
    }
  };

  const alreadyPaid = p && p.status === 'succeeded';

  return (
    <div className="section-page">
      <PageHeader breadcrumbs={[{ label: 'Обучение', to: '/learning' }, { label: 'Оплата курса' }]}
        title="Оплата курса" />
      <div className="section-body">
        <QueryBoundary loading={q.loading && !p} error={q.error} onRetry={q.reload}>
          {p && (
            <div className="buy-card">
              <div className="buy-card__head">
                <span className="buy-card__glyph"><Icon name="sparkles" size={22} /></span>
                <div>
                  <div className="t-eyebrow text-dim">Демо-оплата (тестовый режим)</div>
                  <div className="t-h3">{p.course_title}</div>
                </div>
              </div>
              <div className="buy-card__rows">
                <div className="buy-card__row"><span>Курс</span><span>{p.course_title}</span></div>
                {/* PL-12: страница оплаты обязана называть операцию своим именем.
                    На доплате «Доступ: постоянный» и слово «покупка» читались как
                    вторая полная оплата курса, который у человека уже открыт. */}
                {p.kind === 'upgrade' ? (
                  <>
                    <div className="buy-card__row">
                      <span>Операция</span>
                      <span>{p.tariff_title ? `Доплата до тарифа «${p.tariff_title}»` : 'Доплата до старшего тарифа'}</span>
                    </div>
                    {p.from_tariff_title && (
                      <div className="buy-card__row">
                        <span>Текущий тариф</span><span>{p.from_tariff_title}</span>
                      </div>
                    )}
                    <div className="buy-card__row">
                      <span>К оплате</span>
                      <span className="num">{money(p.amount_rub)}</span>
                    </div>
                    <div className="buy-card__row"><span>Счёт</span><span className="num">{p.invoice_id}</span></div>
                  </>
                ) : (
                  <>
                    <div className="buy-card__row"><span>Сумма</span><span className="num">{money(p.amount_rub)}</span></div>
                    <div className="buy-card__row"><span>Счёт</span><span className="num">{p.invoice_id}</span></div>
                    {p.tariff_title
                      ? <div className="buy-card__row"><span>Тариф</span><span>{p.tariff_title}</span></div>
                      : <div className="buy-card__row"><span>Доступ</span><span>постоянный</span></div>}
                  </>
                )}
              </div>

              {alreadyPaid ? (
                <div className="buy-card__actions">
                  <span className="t-caption text-mute"><Icon name="check-circle" size={14} /> Уже оплачено</span>
                  <Button variant="primary" onClick={() => navigate(`/learning/c/${p.course_id}`)}>Открыть курс</Button>
                </div>
              ) : (
                <div className="buy-card__actions">
                  <Button variant="secondary" loading={busy === 'fail'} onClick={() => act('fail')}>Отказаться</Button>
                  <Button variant="primary" loading={busy === 'success'} onClick={() => act('success')}>
                    {p.kind === 'upgrade' ? 'Доплатить' : 'Оплатить'} · {money(p.amount_rub)}
                  </Button>
                </div>
              )}
              <p className="t-caption text-mute">Это тестовый платёжный шлюз. На проде оплата проходит через Робокассу.</p>
            </div>
          )}
        </QueryBoundary>
      </div>
    </div>
  );
}

export { CourseBuyPage };
